import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Node,
  NodeTypes,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';
import { Box, IconButton, styled, Tooltip } from '@mui/material';
import WorkflowStepEdge from './WorkflowStepEdge';
import { WorkflowActionType } from '../../../data/models/workflow.model';
import workflowChartUtils from '../../../services/workflowChartUtils';
import { useTranslation } from 'react-i18next';
import WorkflowStepNode, { WorkflowStepNodeData } from './WorkflowStepNode';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { isEqual } from 'lodash';
import WorkflowAddButtonNode from './WorkflowAddButtonNode';
import WorkflowBranchingEdge from './WorkflowBranchingEdge';
import { ConstructionOutlined } from '@mui/icons-material';
import WorkflowSmallStepNode from './WorkflowSmallStepNode';

export interface WorkflowTransition {
  id: string;
  name?: string;
  level: number;
}

export interface WorkflowChartItem {
  id: string;
  title: string;
  actions: string[];
  row: number;
  colInfo?: { col: number; totalCols: number };
  parentId?: string;
  onSuccess?: WorkflowTransition;
  onFailure?: WorkflowTransition;
  onCancel?: WorkflowTransition;
  onNext?: WorkflowTransition[];
  isAddButton?: boolean;
}

export interface WorkflowFlowChartProps {
  items: WorkflowChartItem[];
  selectedId: string;
  handleStepClick: (id: string) => boolean;
  handleAddStepClick: () => void;
  handleAddStepToBranchClick?: (branchName: string, parentId: string) => void;
  removedChartItemId?: string;
  showAddStepButton?: boolean;
}

const PREFIX = 'WorkflowFlowChart';

const classes = {
  container: `${PREFIX}-container`,
  addIconContainer: `${PREFIX}-addIconContainer`,
  addIcon: `${PREFIX}-addIcon`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  [`& .${classes.addIconContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
  },
  [`& .${classes.addIcon}`]: {
    color: theme.palette.primary.main,
    marginRight: 35,
  },
}));

export const nodeName = 'workflowStepNode';
export const smallNodeName = 'workflowSmallStepNode';
export const addButtonNodeName = 'workflowAddButtonNode';
export const edgeName = 'workflowStepEdge';
export const branchingEdgeName = 'workflowBranchingEdge';
export const nodeGap = 70;

const WorkflowFlowChart = ({
  items,
  selectedId,
  handleStepClick,
  handleAddStepClick,
  handleAddStepToBranchClick,
  removedChartItemId,
  showAddStepButton = true,
}: WorkflowFlowChartProps) => {
  const { t } = useTranslation('common');
  const nodeTypes: NodeTypes = {
    [nodeName]: WorkflowStepNode,
    [smallNodeName]: WorkflowSmallStepNode,
    [addButtonNodeName]: WorkflowAddButtonNode,
  };
  const edgeTypes = {
    [edgeName]: WorkflowStepEdge,
    [branchingEdgeName]: WorkflowBranchingEdge,
  };
  const initialNodes = [
    {
      id: '-1',
      position: { x: 0, y: 0 },
      type: nodeName,
      data: {
        title: '',
        actions: [''],
        row: 0,
      },
    },
  ]; // Need placeholder node since initial nodes cannot be empty array
  const initialEdges = [
    {
      id: 'placeholder',
      source: '0',
      target: '0',
      label: '',
      type: edgeName,
      data: {
        label: '',
        status: WorkflowActionType.Succeed,
      },
      selectable: false,
    },
  ]; // Need placeholder node since initial edges cannot be empty array
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [nodeHeights, setNodeHeights] = useState<{ [key: string]: number }>({});
  const [flowChartHeight, setFlowChartHeight] = useState(600);
  const [nodeCount, setNodeCount] = useState(0);

  // Handler for height changes
  const handleNodeHeightChange = useCallback(
    (nodeId: string, height: number) => {
      setNodeHeights((prev: any) => {
        // Only update if height actually changed
        if (prev[nodeId] !== height) {
          const newHeights = { ...prev, [nodeId]: height };
          return newHeights;
        }
        return prev;
      });
    },
    [],
  );

  // Update node positions when heights change
  useEffect(() => {
    if (Object.keys(nodeHeights).length === 0) return;

    // Create a copy of the current nodes
    const updatedNodes = nodes.map((node) => ({ ...node }));
    let currentY = 0;
    let groupedNodes = workflowChartUtils.groupByRow(
      updatedNodes,
      (item) => item.data.row,
    );

    groupedNodes.forEach((nodes, index) => {
      let rowHeight = 0;
      nodes.forEach((node, itemIndex) => {
        node.position = {
          ...node.position,
          y: currentY,
        };
        const nodeHeight = nodeHeights[node.id] || 0;
        if (nodeHeight > rowHeight) {
          rowHeight = nodeHeight;
        }
      });
      currentY += rowHeight + nodeGap;
    });
    setFlowChartHeight(currentY - nodeGap + 50);
    // React flow chart can get corrupted if the height
    // isnt set before updating the nodes
    setTimeout(() => {
      setNodes(updatedNodes);
    }, 0);
  }, [nodeHeights, handleNodeHeightChange, nodeCount]);

  const nodesAndEdges = useMemo(() => {
    let nodes = workflowChartUtils.getNodes(items, selectedId, nodeHeights);
    nodes.forEach((node) => {
      node.data.onHeightChange = handleNodeHeightChange;
    });

    return {
      nodes,
      edges: workflowChartUtils.getEdges(items, t),
    };
  }, [items, selectedId]);

  useEffect(() => {
    setNodes(nodesAndEdges.nodes);
    setEdges(nodesAndEdges.edges);
    const newNodeCount = nodesAndEdges.nodes.length;
    if (newNodeCount !== nodeCount) {
      setNodeCount(newNodeCount);
    }
  }, [nodesAndEdges]);

  useEffect(() => {
    if (removedChartItemId) {
      const updatedNodeHeights = {
        ...nodeHeights,
        [removedChartItemId]: 0,
      };
      setNodeHeights(updatedNodeHeights);
    }
  }, [removedChartItemId]);

  // Handler for node clicks
  const onNodeClick = (_: React.MouseEvent, node: Node) => {
    if (node.data.isAddButton) {
      handleAddStepToBranchClick?.(
        node.data.title as string,
        node.data.parentId as string,
      );
      return;
    }
    const stepChanged = handleStepClick(node.id);
    if (!stepChanged) {
      setNodes((nodes) => {
        const updatedNodes = nodes.map((node, index) => ({
          ...node,
          selected: node.id === selectedId,
        }));
        return updatedNodes;
      });
    }
  };

  const onPaneClick = useCallback(() => {
    // Prevent clicks outside of the node from de-selecting it
    setTimeout(() => {
      setNodes(nodes);
    }, 0);
  }, [nodes, setNodes]);

  return (
    <StyledBox>
      <Box className={classes.container} sx={{ height: flowChartHeight }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          zoomOnScroll={false}
          panOnScroll={false}
          panOnDrag={false}
          zoomOnDoubleClick={false}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
        />
      </Box>
      {showAddStepButton && (
        <Box className={classes.addIconContainer}>
          <Tooltip title={t('workflows.addWorkflowStep')}>
            <IconButton
              aria-label="add workflow"
              className={classes.addIcon}
              onClick={() => handleAddStepClick()}>
              <AddCircleOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </StyledBox>
  );
};

export const MemoizedWorkflowFlowChart = memo(
  WorkflowFlowChart,
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);
